import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { setUserId as setAnalyticsUserId } from "firebase/analytics";
import posthog from "posthog-js";

import { firebaseAuth, getFirebase } from "../configs/firebase";
import { AppThunk } from "../store";
import { pomeriumApi } from "../store/pomeriumApi";
import { CurrentState } from "../types/current";
import { OrgAndCluster } from "../types/organizations";

const initialState: CurrentState = {
  organizationId: "",
  clusterId: "",
  userId: "",
  flavor: undefined,
};

type OptionalFlavorOrgAndCluster = Omit<OrgAndCluster, "flavor"> &
  Partial<Pick<OrgAndCluster, "flavor">>;

const slice = createSlice({
  name: "current",
  initialState,
  reducers: {
    setUserId(state: CurrentState, action: PayloadAction<string>): void {
      state.userId = action.payload;
    },
    setClusterAndOrg(
      state: CurrentState,
      action: PayloadAction<OptionalFlavorOrgAndCluster>,
    ): void {
      const { organizationId, clusterId, flavor } =
        action.payload as OptionalFlavorOrgAndCluster;
      state.organizationId = organizationId;
      state.clusterId = clusterId;

      if (flavor) {
        state.flavor = flavor;
        localStorage.setItem(state.userId + "_clusterFlavor", flavor);
      }

      localStorage.setItem(state.userId + "_organizationId", organizationId);
      localStorage.setItem(state.userId + "_clusterId", clusterId);
    },
    setOrgId(state: CurrentState, action: PayloadAction<string>): void {
      state.organizationId = action.payload;
    },
  },
});

export const { reducer, actions } = slice;

export const {
  setOrgId,
  setUserId,
  setClusterAndOrg: setClusterAndOrgAction,
} = actions;

/**
 * Resolve flavor from organizations.clustersByOrg if not provided
 *
 * @param payload
 * @returns a thunk that resolves the flavor and dispatches setClusterAndOrgAction
 */
export const setClusterAndOrgResolved =
  (payload: OptionalFlavorOrgAndCluster): AppThunk =>
  async (dispatch, getState): Promise<void> => {
    if (payload.flavor) {
      dispatch(setClusterAndOrgAction(payload));
      return;
    }

    // Cluster flavor not provided, attempt to resolve it
    const state = getState();
    const clustersByOrg = state.organizations.clustersByOrg ?? {};
    const cluster = clustersByOrg[payload.organizationId]?.find(
      (c) => c.id === payload.clusterId,
    );

    if (!cluster) {
      dispatch(setClusterAndOrgAction(payload));
      return;
    }

    dispatch(setClusterAndOrgAction({ ...payload, flavor: cluster.flavor }));
  };

// Export thunk as the default runtime helper named `setClusterAndOrg` so existing runtime imports don't need changes
// Re-export the thunk as `setClusterAndOrg` so runtime callers can remain unchanged
export const setClusterAndOrg = setClusterAndOrgResolved;
export const logout =
  (): AppThunk =>
  async (dispatch): Promise<void> => {
    const { analytics } = getFirebase();
    await firebaseAuth.signOut();
    dispatch(pomeriumApi.util.resetApiState());
    if (analytics) {
      setAnalyticsUserId(analytics, "");
    }
    posthog.reset();
  };
